import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import * as Icons from 'react-icons/fa'; 
import { MdOutlineClose, MdColorLens, MdViewSidebar, MdCheck, MdLayers, MdBusiness } from 'react-icons/md';

// --- FIREBASE IMPORTS ---
import { db, auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';

// --- PAGE IMPORTS ---
import Chat from './Chat';
import Broadcast from './Broadcast';
import Dashboard from './Dashboard';
import Templates from './Templates';
import ConnectAPI from './ConnectAPI'; 
import Login from './Login';
import BotManager from './BotManager';
import Analytics from './Analytics'; 
import Contacts from './Contacts'; 
import Pipeline from './Pipeline';
import Staff from './Staff'; 
import PrivacyPolicy from './PrivacyPolicy'; 
import Terms from './Terms';
import DataDeletion from './DataDeletion';
import SuperAdmin from './SuperAdmin';
import BillingAnalytics from './BillingAnalytics';
import DatabaseManager from './DatabaseManager'; 
import EmbeddedSetup from './EmbeddedSetup'; 
import ConnectManager from './ConnectManager'; 
import DripCampaign from './DripCampaign';

// --- THEME SETTINGS MODAL (PRO DESIGN STUDIO) ---
function ThemeSettings({ closeSettings, saveSettings, currentName, sidebarColor, setSidebarColor, sidebarLayout, setSidebarLayout }) {
  const [tempName, setTempName] = useState(currentName);
  
  // 18 Pro Colors
  const colors = [
    '#0f172a', '#1e3a8a', '#064e3b', '#4c1d95', '#7f1d1d', '#111827', 
    '#0ea5e9', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#64748b',
    'linear-gradient(135deg, #0f172a, #1e293b)', // Midnight
    'linear-gradient(135deg, #1e40af, #2563eb)', // Ocean
    'linear-gradient(135deg, #047857, #10b981)', // Emerald
    'linear-gradient(135deg, #6b21a8, #9333ea)', // Amethyst
    'linear-gradient(135deg, #be123c, #e11d48)', // Ruby
    'linear-gradient(135deg, #000000, #434343)'  // Carbon
  ];

  const layouts = [
    { id: 'classic', name: 'Classic Enterprise', desc: 'Standard full-height left sidebar' },
    { id: 'compact', name: 'Icon Rail', desc: 'Maximizes workspace width' },
    { id: 'floating', name: 'Floating Island', desc: 'Detached macOS-style card' },
    { id: 'topnav', name: 'Horizontal SaaS', desc: 'Moves navigation to the top bar' },
    { id: 'glass', name: 'Frosted Glass', desc: 'Translucent iOS aesthetic' },
    { id: 'minimal', name: 'Linear Minimalist', desc: 'Razor-sharp clean lines' }
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#ffffff', width: '95%', maxWidth: '700px', borderRadius: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.3)', overflow: 'hidden', animation: 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        
        {/* Header */}
        <div style={{ padding: '25px 35px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '22px', fontWeight: '800', letterSpacing: '-0.5px' }}>
            <MdLayers style={{ color: '#3b82f6', fontSize: '28px' }} /> Pro Design Studio
          </h3>
          <div onClick={closeSettings} style={{ width: '36px', height: '36px', background: '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#fecaca'} onMouseLeave={e => e.currentTarget.style.background = '#e2e8f0'}>
             <MdOutlineClose style={{ fontSize: '22px', color: '#64748b' }} />
          </div>
        </div>

        <div style={{ padding: '35px', overflowY: 'auto', flex: 1 }}>
          
          {/* Business Name Picker */}
          <div style={{ marginBottom: '35px' }}>
            <div style={{ fontSize: '15px', fontWeight: '800', color: '#334155', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              <MdBusiness size={22} color="#10b981" /> 1. Brand Workspace Name
            </div>
            <input 
              type="text" 
              value={tempName} 
              onChange={(e) => setTempName(e.target.value)} 
              placeholder="e.g. Rebel Spa CRM"
              style={{ width: '100%', padding: '16px 20px', borderRadius: '12px', border: '2px solid #e2e8f0', outline: 'none', fontSize: '16px', fontWeight: '700', color: '#0f172a', transition: 'border 0.2s', background: '#f8fafc' }}
              onFocus={e => e.currentTarget.style.borderColor = '#3b82f6'}
              onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>This name will replace "Tesla CRM" in the main navigation menu.</p>
          </div>

          {/* Architecture Picker */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{ fontSize: '15px', fontWeight: '800', color: '#334155', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              <MdViewSidebar size={22} color="#f59e0b" /> 2. Select App Architecture
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {layouts.map(layout => (
                <div 
                  key={layout.id} onClick={() => setSidebarLayout(layout.id)}
                  style={{ 
                    border: sidebarLayout === layout.id ? '2px solid #3b82f6' : '2px solid #e2e8f0', 
                    borderRadius: '16px', padding: '16px', cursor: 'pointer', 
                    display: 'flex', alignItems: 'center', gap: '15px',
                    transition: 'all 0.2s', background: sidebarLayout === layout.id ? '#eff6ff' : '#fff',
                    boxShadow: sidebarLayout === layout.id ? '0 4px 15px rgba(59, 130, 246, 0.15)' : '0 2px 5px rgba(0,0,0,0.02)'
                  }}
                  onMouseEnter={e => { if(sidebarLayout !== layout.id) e.currentTarget.style.borderColor = '#cbd5e1' }}
                  onMouseLeave={e => { if(sidebarLayout !== layout.id) e.currentTarget.style.borderColor = '#e2e8f0' }}
                >
                  <div style={{ width: '45px', height: '45px', background: sidebarLayout === layout.id ? '#3b82f6' : '#f1f5f9', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                     {sidebarLayout === layout.id ? <MdCheck size={24} color="#fff" /> : <MdViewSidebar size={22} color="#94a3b8" />}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '800', color: sidebarLayout === layout.id ? '#1e3a8a' : '#334155' }}>{layout.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px', fontWeight: '500' }}>{layout.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Color Picker */}
          <div>
            <div style={{ fontSize: '15px', fontWeight: '800', color: '#334155', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              <MdColorLens size={22} color="#8b5cf6" /> 3. Choose Theme Color
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px' }}>
              {colors.map((c, i) => (
                <div 
                  key={i} onClick={() => setSidebarColor(c)}
                  style={{
                    height: '50px', borderRadius: '14px', background: c, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: sidebarColor === c ? '3px solid #10b981' : '3px solid transparent',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0) scale(1)'}
                >
                  {sidebarColor === c && <MdCheck color="white" size={24} />}
                </div>
              ))}
            </div>
          </div>

        </div>
        
        {/* Footer */}
        <div style={{ padding: '20px 35px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => saveSettings(tempName)} style={{ background: '#0f172a', color: 'white', border: 'none', padding: '14px 35px', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 15px rgba(15, 23, 42, 0.3)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#1e293b'} onMouseLeave={e => e.currentTarget.style.background = '#0f172a'}>
            Save & Apply Layout
          </button>
        </div>
      </div>
    </div>
  );
}

// --- DYNAMIC ARCHITECTURE SIDEBAR ---
function Navigation({ isMobile, sidebarOpen, setSidebarOpen, sidebarColor, sidebarLayout, openSettings, user, businessName }) {
  const location = useLocation();
  
  // Mobile overrides everything to classic off-canvas drawer to prevent breaking UI
  const activeLayout = isMobile ? 'classic' : sidebarLayout;

  const isCompact = activeLayout === 'compact';
  const isFloating = activeLayout === 'floating';
  const isTopNav = activeLayout === 'topnav';
  const isGlass = activeLayout === 'glass';
  const isMinimal = activeLayout === 'minimal';

  const menuItems = [
    { section: 'MAIN', items: [
      { path: '/dashboard', name: 'Dashboard', icon: <Icons.FaChartPie /> },
      { path: '/analytics', name: 'ROI Tracker', icon: <Icons.FaChartLine /> },
    ]},
    { section: 'COMMUNICATION', items: [
      { path: '/chat', name: 'Live Chat', icon: <Icons.FaCommentDots /> },
      { path: '/broadcast', name: 'Broadcasting', icon: <Icons.FaBroadcastTower /> },
      { path: '/drip', name: 'Drip Campaigns', icon: <Icons.FaClock /> }, 
      { path: '/templates', name: 'Templates', icon: <Icons.FaFileAlt /> },
      { path: '/bot', name: 'Bot Manager', icon: <Icons.FaRobot /> },
    ]},
    { section: 'MANAGEMENT', items: [
      { path: '/contacts', name: 'Smart Contacts', icon: <Icons.FaAddressBook /> },
      { path: '/database', name: 'Audience Data', icon: <Icons.FaDatabase /> }, 
      { path: '/pipeline', name: 'Booking Pipeline', icon: <Icons.FaTrello /> },
      { path: '/connect', name: 'Connect API', icon: <Icons.FaPlug /> }, 
      { path: '/force-connect', name: 'Force API', icon: <Icons.FaLink /> }, 
      { path: '/whatsapp-setup', name: 'WhatsApp Setup', icon: <Icons.FaMagic /> }, 
      { path: '/users', name: 'User Manager', icon: <Icons.FaUsers /> },
      { path: '/billing', name: 'Billing Usage', icon: <Icons.FaWallet /> },
    ]}
  ];

  const handleLogout = () => { if(window.confirm("Are you sure you want to log out?")) signOut(auth); };

  // ==========================================
  // LAYOUT 4: TOP NAVIGATION (HORIZONTAL SAAS)
  // ==========================================
  if (isTopNav) {
    return (
      <div style={{ width: '100%', height: '75px', background: sidebarColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 25px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 50, color: 'white' }}>
        {/* TopNav Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginRight: '30px' }}>
          <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '8px', borderRadius: '10px', display: 'flex', boxShadow: '0 2px 8px rgba(16,185,129,0.4)' }}>
            <Icons.FaWhatsapp style={{ fontSize: '22px', color: 'white' }} />
          </div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', letterSpacing: '-0.5px', whiteSpace: 'nowrap' }}>{businessName}</h2>
        </div>

        {/* TopNav Horizontal Links */}
        <div style={{ display: 'flex', gap: '8px', flex: 1, overflowX: 'auto', padding: '0 10px', scrollbarWidth: 'none' }}>
          {menuItems.flatMap(section => section.items).map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} style={{ 
                textDecoration: 'none', color: isActive ? '#fff' : 'rgba(255,255,255,0.7)', 
                padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', 
                background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent', borderRadius: '12px', 
                fontWeight: isActive ? '700' : '600', transition: 'all 0.2s', whiteSpace: 'nowrap'
              }}
              onMouseEnter={e => { if(!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
              onMouseLeave={e => { if(!isActive) e.currentTarget.style.background = 'transparent' }}>
                <span style={{ fontSize: '16px', color: isActive ? '#10b981' : 'inherit' }}>{item.icon}</span>
                <span style={{ fontSize: '14px' }}>{item.name}</span>
              </Link>
            )
          })}
        </div>

        {/* TopNav Profile & Settings */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginLeft: '20px' }}>
          <Icons.FaCog onClick={openSettings} style={{ fontSize: '22px', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', transition: 'transform 0.2s' }} onMouseEnter={e=>e.currentTarget.style.transform='rotate(90deg)'} onMouseLeave={e=>e.currentTarget.style.transform='rotate(0)'} />
          <Icons.FaSignOutAlt onClick={handleLogout} style={{ fontSize: '20px', color: '#fca5a5', cursor: 'pointer' }} />
          {user && (
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', textTransform: 'uppercase', border: '2px solid rgba(255,255,255,0.4)' }}>
              {user.email.charAt(0)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // ALL OTHER LAYOUTS (VERTICAL SIDEBARS)
  // ==========================================
  const sidebarWidth = isCompact ? '90px' : '270px';
  
  const minimalBg = '#09090b'; 
  const actualBg = isMinimal ? minimalBg : (isGlass ? 'rgba(15, 23, 42, 0.45)' : sidebarColor);

  const baseStyle = {
    width: sidebarWidth, 
    background: actualBg, 
    backdropFilter: isGlass ? 'blur(20px)' : 'none',
    color: '#fff', 
    height: isFloating ? 'calc(100vh - 30px)' : '100vh', 
    margin: isFloating ? '15px' : '0',
    borderRadius: isFloating ? '24px' : '0',
    display: 'flex', flexDirection: 'column', 
    boxShadow: isFloating ? '0 20px 40px rgba(0,0,0,0.2)' : '4px 0 25px rgba(0,0,0,0.1)', 
    position: isMobile ? 'fixed' : 'relative',
    left: isMobile ? (sidebarOpen ? '0' : '-300px') : '0',
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    zIndex: 20,
    borderRight: isMinimal ? '1px solid #27272a' : (isGlass ? '1px solid rgba(255,255,255,0.1)' : 'none')
  };

  return (
    <>
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 15 }} />
      )}
      
      <div style={baseStyle}>
        {/* Logo Section */}
        <div style={{ padding: isCompact ? '30px 0' : '30px 25px', display: 'flex', justifyContent: isCompact ? 'center' : 'flex-start', alignItems: 'center', gap: '12px', borderBottom: isMinimal ? '1px solid #27272a' : '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '10px', borderRadius: '12px', display: 'flex', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}>
            <Icons.FaWhatsapp style={{ fontSize: '24px', color: 'white' }} />
          </div>
          {!isCompact && <h2 style={{ margin: 0, color: '#f8fafc', fontSize: '22px', fontWeight: '900', letterSpacing: '-0.5px' }}>{businessName}</h2>}
        </div>

        {/* Links Section */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isCompact ? '20px 10px' : '25px 20px', overflowX: 'hidden' }}>
          {menuItems.map((section, idx) => (
            <div key={idx} style={{ marginBottom: '30px' }}>
              {!isCompact && <div style={{ fontSize: '11px', fontWeight: '800', color: isMinimal ? '#52525b' : 'rgba(255,255,255,0.4)', marginBottom: '15px', paddingLeft: '15px', letterSpacing: '1.5px' }}>{section.section}</div>}
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                
                // Specific styling for "Minimal/Linear" layout
                if (isMinimal) {
                  return (
                    <Link key={item.path} to={item.path} title={isCompact ? item.name : ""} onClick={() => isMobile && setSidebarOpen(false)} style={{ 
                      textDecoration: 'none', color: isActive ? '#fff' : '#a1a1aa', 
                      padding: isCompact ? '14px 0' : '12px 15px', display: 'flex', alignItems: 'center', justifyContent: isCompact ? 'center' : 'flex-start', gap: '15px', 
                      background: 'transparent', transition: 'all 0.2s', fontWeight: isActive ? '700' : '500',
                      borderLeft: isCompact ? 'none' : (isActive ? '3px solid #10b981' : '3px solid transparent'),
                      marginBottom: '4px'
                    }}
                    onMouseEnter={e => { if(!isActive) e.currentTarget.style.color = '#fff' }}
                    onMouseLeave={e => { if(!isActive) e.currentTarget.style.color = '#a1a1aa' }}>
                      <span style={{ fontSize: '18px', color: isActive ? '#10b981' : 'inherit' }}>{item.icon}</span>
                      {!isCompact && <span style={{ fontSize: '15px', whiteSpace: 'nowrap' }}>{item.name}</span>}
                    </Link>
                  );
                }

                // Styling for all other vertical layouts
                return (
                  <Link key={item.path} to={item.path} title={isCompact ? item.name : ""} onClick={() => isMobile && setSidebarOpen(false)} style={{ 
                    textDecoration: 'none', color: isActive ? '#fff' : 'rgba(255,255,255,0.7)', 
                    padding: isCompact ? '14px 0' : '12px 15px', display: 'flex', alignItems: 'center', justifyContent: isCompact ? 'center' : 'flex-start', gap: '15px', 
                    background: isActive ? (isGlass ? 'rgba(255,255,255,0.15)' : 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)') : 'transparent',
                    borderRadius: '12px', transition: 'all 0.2s ease', fontWeight: isActive ? '700' : '500',
                    borderLeft: isCompact ? 'none' : (isActive ? '4px solid #10b981' : '4px solid transparent'),
                    marginBottom: '6px'
                  }}
                  onMouseEnter={e => { if(!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                  onMouseLeave={e => { if(!isActive) e.currentTarget.style.background = 'transparent' }}>
                    <span style={{ fontSize: '18px', color: isActive ? '#10b981' : 'inherit', transition: 'color 0.2s' }}>{item.icon}</span>
                    {!isCompact && <span style={{ fontSize: '14.5px', whiteSpace: 'nowrap' }}>{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        <div style={{ padding: isCompact ? '20px 10px' : '25px 20px', borderTop: isMinimal ? '1px solid #27272a' : '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button onClick={openSettings} title="Design Studio" style={{ width: '100%', background: isMinimal ? '#18181b' : 'rgba(255,255,255,0.08)', color: '#fff', border: isMinimal ? '1px solid #27272a' : 'none', padding: '14px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = isMinimal ? '#27272a' : 'rgba(255,255,255,0.15)' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = isMinimal ? '#18181b' : 'rgba(255,255,255,0.08)' }}>
            <Icons.FaPaintBrush size={16} color={isMinimal ? '#a1a1aa' : '#fff'} /> {!isCompact && "Design Studio"}
          </button>
          
          <button onClick={handleLogout} title="Log Out" style={{ width: '100%', background: isMinimal ? '#450a0a' : 'rgba(239, 68, 68, 0.15)', color: '#fca5a5', border: isMinimal ? '1px solid #7f1d1d' : 'none', padding: '14px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = isMinimal ? '#450a0a' : 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#fca5a5'; }}>
            <Icons.FaSignOutAlt size={16} /> {!isCompact && "Log Out"}
          </button>
        </div>
      </div>
    </>
  );
}

// --- MASTER APP COMPONENT ---
function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [businessName, setBusinessName] = useState('Tesla CRM'); // DYNAMIC BUSINESS NAME STATE

  const [sidebarColor, setSidebarColor] = useState(() => localStorage.getItem('teslaCrmColor') || '#0f172a');
  const [sidebarLayout, setSidebarLayout] = useState(() => localStorage.getItem('teslaCrmLayout') || 'classic');

  useEffect(() => {
    localStorage.setItem('teslaCrmColor', sidebarColor);
    localStorage.setItem('teslaCrmLayout', sidebarLayout);
  }, [sidebarColor, sidebarLayout]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && sidebarLayout !== 'topnav') setSidebarOpen(true);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarLayout]);

  // ==========================================
  // 🔥 NEW: WHITE-LABEL DOMAIN ROUTER 🔥
  // ==========================================
  useEffect(() => {
    const fetchDomainBranding = async () => {
      const currentDomain = window.location.hostname;
      
      // Skip lookup if it's your main domain or localhost
      if (currentDomain === 'teslacrmconnect.com' || currentDomain === 'localhost') {
        return; 
      }

      try {
        const q = query(collection(db, "businesses"), where("customDomain", "==", currentDomain));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const bizData = querySnapshot.docs[0].data();
          if (bizData.businessName) {
            setBusinessName(bizData.businessName);
            document.title = `${bizData.businessName} - CRM Portal`;
          }
          if (bizData.themeColor) {
            setSidebarColor(bizData.themeColor);
          }
        }
      } catch (error) {
        console.error("White-label fetch error:", error);
      }
    };

    fetchDomainBranding();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const docRef = doc(db, "businesses", currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && docSnap.data().businessName) {
            setBusinessName(docSnap.data().businessName);
            document.title = `${docSnap.data().businessName} - CRM`;
          }
        } catch (error) {
          console.error("Error fetching business name:", error);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // SAVE NEW SETTINGS TO FIRESTORE
  const handleSaveSettings = async (newName) => {
    setBusinessName(newName || 'Tesla CRM');
    setShowSettings(false);
    if (user) {
      try {
        await setDoc(doc(db, "businesses", user.uid), { businessName: newName || 'Tesla CRM' }, { merge: true });
        document.title = `${newName || 'Tesla CRM'} - CRM`;
      } catch (error) {
        console.error("Failed to save business name:", error);
      }
    }
  };

  if (loading) return <div style={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontWeight: 'bold' }}>Loading {businessName}...</div>;

  const publicPath = window.location.pathname;
  if (['/privacy', '/privacy-policy'].includes(publicPath)) return <PrivacyPolicy />;
  if (['/terms', '/terms-of-service'].includes(publicPath)) return <Terms />;
  if (['/data-deletion', '/delete-data'].includes(publicPath)) return <DataDeletion />;
  if (!user) return <Login />;

  const isTopNav = sidebarLayout === 'topnav' && !isMobile;
  const isGlass = sidebarLayout === 'glass';
  
  const appBackground = isGlass 
    ? 'url(https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop) center/cover no-repeat fixed' 
    : '#f1f5f9';

  return (
    <Router>
      <div style={{ display: 'flex', flexDirection: isTopNav ? 'column' : 'row', height: '100vh', width: '100vw', background: appBackground, overflow: 'hidden', fontFamily: '"Inter", sans-serif' }}>
        
        <Navigation 
          isMobile={isMobile} 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen} 
          sidebarColor={sidebarColor} 
          sidebarLayout={sidebarLayout}
          openSettings={() => setShowSettings(true)}
          user={user}
          businessName={businessName} 
        />
        
        {showSettings && (
          <ThemeSettings 
            closeSettings={() => setShowSettings(false)}
            saveSettings={handleSaveSettings}
            currentName={businessName} 
            sidebarColor={sidebarColor} setSidebarColor={setSidebarColor}
            sidebarLayout={sidebarLayout} setSidebarLayout={setSidebarLayout}
          />
        )}
        
        {/* Main Content Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: isGlass ? 'rgba(255,255,255,0.85)' : 'transparent', backdropFilter: isGlass ? 'blur(10px)' : 'none' }}>
          
          {!isTopNav && (
            <div style={{ height: '80px', background: isGlass ? 'rgba(255,255,255,0.6)' : '#ffffff', backdropFilter: isGlass ? 'blur(10px)' : 'none', display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-end', padding: '0 35px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', zIndex: 5 }}>
              {isMobile && <div onClick={() => setSidebarOpen(true)} style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: '12px', cursor: 'pointer' }}><Icons.FaBars style={{ fontSize: '20px', color: '#334155' }} /></div>}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                {!isMobile && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Spa Dashboard</div>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>{user.email}</div>
                  </div>
                )}
                <div style={{ width: '50px', height: '50px', borderRadius: '14px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '20px', textTransform: 'uppercase', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)' }}>
                  {user.email.charAt(0)}
                </div>
              </div>
            </div>
          )}
          
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '15px' : '30px' }}>
            <Routes>
              <Route path="/" element={<Navigate to="/chat" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/connect" element={<ConnectAPI />} /> 
              <Route path="/force-connect" element={<ConnectManager />} />
              <Route path="/whatsapp-setup" element={<EmbeddedSetup />} /> 
              <Route path="/templates" element={<Templates />} />
              <Route path="/broadcast" element={<Broadcast />} />
              <Route path="/drip" element={<DripCampaign />} /> 
              <Route path="/chat" element={<div style={{height: '100%', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 15px 40px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.05)'}}><Chat /></div>} />
              <Route path="/bot" element={<BotManager />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/pipeline" element={<Pipeline />} /> 
              <Route path="/users" element={<Staff />} /> 
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/terms-of-service" element={<Terms />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/delete-data" element={<DataDeletion />} />
              <Route path="/god-mode" element={<SuperAdmin />} />
              <Route path="/billing" element={<BillingAnalytics />} /> 
              <Route path="/super-admin" element={<SuperAdmin />} />
              <Route path="/database" element={<DatabaseManager />} /> 
            </Routes>
          </div>
        </div>
      </div>
    </Router>
  );
}

export default App;